import { Application } from '@platform/client';
import { SAPApplicationParameters } from '@platform/client/schemas';
import {
  ErrorBox,
  FormsCheckBox,
  FormsInputDropdown,
  InputDropdownItem,
} from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Aanu-Gopald.svg';

type FormValues = {
  documentReview: boolean;
  referralMethod: string;
};

const SAP_REFERRAL_OPTIONS: InputDropdownItem[] = [
  { text: 'A colleague within your company', textValue: 'A colleage within your company' },
  { text: 'Your manager', textValue: 'Your manager' },
  { text: 'Your Global CSR leader', textValue: 'Your Global CSR leader' },
  { text: 'Your Regional CSR leader', textValue: 'Your Regional CSR leader' },
  {
    text: 'Email communication from your company',
    textValue: 'Email communication from your company',
  },
  { text: 'Internal meeting or town hall', textValue: 'Internal meeting or town hall' },
  { text: 'LinkedIn', textValue: 'LinkedIn' },
  { text: 'Other', textValue: 'Other' },
];

export const SAPSubmission = (props: ApplicationPageProps) => {
  const { onBack, progress } = { ...props };
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const { getApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<SAPApplicationParameters>();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      referralMethod: getApplicationDataByKey('referralMethod') || '',
      documentReview: getApplicationDataByKey('documentReview') || false,
    },
  });

  const queryClient = useQueryClient();

  const submitSAPMutation = Application.useSubmitSapApplication();

  const submit: SubmitHandler<FormValues> = (data) => {
    if (data.documentReview && data.referralMethod) {
      submitSAPMutation.mutate(
        {
          data: {
            ...getApplicationData(),
            referralMethod: data.referralMethod,
            documentReview: data.documentReview,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: Application.getGetProfessionalApplicationStatusQueryKey(),
            });
          },
          onError: () => {
            setFailed(true);
          },
        },
      );
    } else {
      setSubmitted(true);
    }
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Ready to submit? Just one more thing.'
        description='After you submit, your responses are final, so take a moment to make sure everything is just right.'
      >
        <FormsInputDropdown
          control={control}
          name='referralMethod'
          rules={{
            required: 'Select how you found out about this opportunity to continue',
          }}
          label='How did you find out about this opportunity?'
          placeholder='Select one'
          items={SAP_REFERRAL_OPTIONS}
          type='default'
          error={errors.referralMethod}
          inputDisabled={true}
        />
        <div className='flex flex-row items-start justify-start'>
          <FormsCheckBox
            control={control}
            name='documentReview'
            size='md'
            description={
              <div>
                I've reviewed and agree to{' '}
                <a
                  className='underline'
                  href='https://drive.google.com/file/d/1E6cl50FoizvcuMFutKNA4tZpv7ukiZQQ/view?usp=drive_link'
                >
                  the liability release document
                </a>{' '}
                and{' '}
                <a
                  className='underline'
                  href='https://drive.google.com/file/d/15Q_EH7BlthYwvKxhaHaHtRqUXCzi5M_X/view?usp=drive_link'
                >
                  media release document
                </a>
              </div>
            }
          />
        </div>
        {submitted && (
          <ErrorBox showAlertTriangle={true}>
            Agree to the liability release document and media release document to submit your
            application
          </ErrorBox>
        )}
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
